/**
 * @file Nobody walks around empty-handed (issue #122).
 *
 * Throwing a weapon used to leave the player with no `Weapon` component at all,
 * and melee was a hidden branch that fired when that was the case. That made the
 * knife invisible: no model in hand, no HUD card, nothing to tune. Here it is a
 * real equipped weapon like every other, and this system is the one thing that
 * hands it out.
 *
 * Which weapon plays that role is data — `fallback: true` on a row in
 * {@link ../config/Weapons.js} — so this file names no weapon at all.
 *
 * It runs between `ThrowSystem` (15) and `WeaponSystem` (25), so a weapon thrown
 * this frame is replaced within the same frame and the player is never actually
 * seen with empty hands.
 */

import { System } from '../ecs/System.js';
import { Health, PlayerControlled, Transform, Weapon } from '../components/index.js';
import { FALLBACK_WEAPON } from '../config/Weapons.js';
import { equipWeaponOn, releaseHeldWeaponForPolicy } from './InteractionSystem.js';
import { shouldUnequipEmptyWeapon } from '../combat/ammoPolicy.js';

export class FallbackWeaponSystem extends System {
  /** After `ThrowSystem` (15), before `WeaponSystem` (25). */
  static priority = 18;

  /**
   * @param {number} _dt Delta time in seconds.
   * @returns {void}
   */
  update(_dt) {
    const policy = this.ctx.config?.ammo?.emptyMagazinePolicy;

    for (const player of this.world.query(PlayerControlled, Transform, Health)) {
      // A corpse needs no knife, and re-arming one would put a weapon back in
      // the scene for the frame between death and cleanup.
      if (!player.alive || player.get(Health).dead) continue;

      const held = player.get(Weapon);
      if (held && shouldUnequipEmptyWeapon(held, policy)) {
        // Spent gun leaves the hands (and the floor if droppable); knife follows.
        releaseHeldWeaponForPolicy(player, this.ctx);
      }

      if (!FALLBACK_WEAPON || player.has(Weapon)) continue;
      equipFallbackWeapon(player, this.ctx);
    }
  }
}

/**
 * Puts the fallback weapon in a carrier's hands, through the same path a floor
 * pickup and the debug menu use — so the player's layers, the released trigger,
 * `ctx.playerWeaponKey` and the `WEAPON_CHANGED` announcement can never drift
 * between "equipped a gun" and "handed the knife back".
 *
 * The empty-hands check is what keeps the announcement to the transition rather
 * than to every frame.
 *
 * @param {import('../ecs/Entity.js').Entity} carrier Entity to arm.
 * @param {import('../core/Context.js').GameContext} ctx Shared game context.
 * @returns {import('../components/Weapon.js').Weapon | null} The equipped
 *   weapon, or null when there is no fallback row or the carrier is already armed.
 */
export function equipFallbackWeapon(carrier, ctx) {
  if (!FALLBACK_WEAPON || !carrier || carrier.has(Weapon)) return null;
  return equipWeaponOn(carrier, FALLBACK_WEAPON, ctx);
}
